import React, { useCallback, useEffect, useState } from 'react';
import styles from './StoreList.module.css';
import { CustomLoader, CustomTitle, CustomCardList, CustomTextMarkdown, CustomText, CustomFooter, CustomMoreSection, CustomPagination, CustomNoData, CustomInputText } from '../../../components';
import { useTranslation } from 'react-i18next';
import { getCategories, getProjectList, toggleLikeProject } from '../../../services/storeService';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { useNavigate } from 'react-router-dom';
import { getStoreContent } from '../../../services/contentService';
import CustomSelectInput from '../../../components/customSelectInput/CustomSelectInput';
import {ReactComponent as SearchIcon} from '../../../assets/icons/icon_search.svg';

const Store = () => {
    const ELEMENTS_BY_PAGE = 12;

    const {t} = useTranslation();
    const navigate = useNavigate();
    const userId : number = useSelector((state: RootState) => state.auth.userId);

    const [isLoading, setIsLoading] = useState(true);
    const [isProjectLoading, setIsProjectLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState<number>(0);
    const [search, setSearch] = useState<string>('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const [pageContent, setPageContent] = useState<any>(null);
    const [categories, setCategories] = useState<any[]>([]);
    const [selectedCategories, setSelectedCategories] = useState<string>('');
    const [projectList, setProjectList] = useState<CustomCardType[]>([]);

    const handleGetStoreContent = async () => {
        setIsLoading(true);

        const content = await getStoreContent();
        setPageContent(content);

        setIsLoading(false);
    }

    const handleGetProjectsList = useCallback(async (category: string) => {
        const res = await getProjectList(category, currentPage*ELEMENTS_BY_PAGE, ELEMENTS_BY_PAGE, debouncedSearch);
        const projectList = res.data;

        const formatedProjectList: CustomCardType[] = projectList?.map((item: any): any => ({
            ...item,
            isLiked: item.likes.some((item: any) => item.id === userId),
        }));

        setPageNumber(Math.ceil(res.meta.pagination.total / ELEMENTS_BY_PAGE));
        setProjectList(formatedProjectList);
    }, [currentPage, userId, debouncedSearch]);

    const handleGetCategories = async () => {
        const categories = await getCategories();

        const formatedCategories: ListElementType[] = categories.map((category: any, index: number) => ({
            id: index,
            title: category.title,
            description: category.title
        }));

        setCategories(formatedCategories);
    }

    const handleGetData = useCallback(async () => {
        setIsProjectLoading(true);
        
        await handleGetCategories();
        await handleGetProjectsList(selectedCategories);

        setIsProjectLoading(false);
    }, [handleGetProjectsList, selectedCategories]);

    useEffect(() => {
        handleGetData();
    }, [handleGetData, currentPage, debouncedSearch]);

    useEffect(() => {
        handleGetStoreContent();
    }, []);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(search);
        }, 500);

        return () => clearTimeout(handler);
    }, [search]);

    const handleLikeProject = (project: any) => {
        if (userId) {
            setProjectList((prevList: any[])=>
                prevList.map(currentProject =>
                    currentProject.documentId === project.documentId
                    ? {
                        ...currentProject,
                        isLiked: !currentProject.isLiked,
                    }
                    : currentProject
                )
            );
    
            toggleLikeProject(project, userId);
        }
    }

    const handleShareProject = async (url: string, title: string, description: string) => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: title,
                    text: description,
                    url: `${process.env.REACT_APP_API_BASE_URL}/store/${url}`,
                });
            } catch (error) {
                console.error(error);
            }
        } else {
            alert(t('store.share_not_allowed'));
        }
    }

    const handleUpdateProjectList = async (value: string) => {
        setIsProjectLoading(true);
        setSelectedCategories(value);

        await handleGetProjectsList(value);

        setIsProjectLoading(false);
    }

    const goToProductPage = (projectId: number) => {
        navigate(`/store/${projectId}`);
    }

    return (
        <div className={styles.container}>
            { !isLoading &&
                <>
                    <div className={styles.A_block}>
                        <CustomTitle isSubtitle className={styles.subtitle}>{pageContent.subtitle}</CustomTitle>
                        <CustomTitle center>{pageContent.title}</CustomTitle>
                        <CustomTextMarkdown className={styles.paragraph}>{pageContent.paragraph}</CustomTextMarkdown>
                    </div>

                    <div className={styles.store_container}>
                        <div className={styles.filters_container}>
                            <div className={styles.categories_filter}>
                                <CustomText>{t('store.filter_by')}</CustomText>
                                <div className={styles.select_input_container}>
                                    <CustomSelectInput
                                        id="categories"
                                        allowEmpty
                                        emptyLabel={t('store.no_filter')}
                                        onChange={(value: string) => handleUpdateProjectList(value)}
                                        list={categories}
                                        value={selectedCategories}
                                        disabled={isProjectLoading}
                                        className={styles.select_input}
                                    />
                                </div>
                            </div>

                            <div className={styles.search_filter}>
                                <CustomInputText
                                    id="search"
                                    startIcon={<SearchIcon />}
                                    placeholder={t('general.search')}
                                    textValue={search}
                                    setTextValue={setSearch}
                                    disabled={isProjectLoading}
                                    className={styles.search_input}
                                />
                            </div>
                        </div>

                        { !isProjectLoading &&
                            <>
                                <CustomCardList
                                    imagesAlt="" 
                                    list={projectList}
                                    onLike={handleLikeProject}
                                    onClick={goToProductPage}
                                    onShare={handleShareProject}
                                    hoverText={t('store.see_project')}
                                />

                                { projectList.length === 0 &&
                                    <CustomNoData message={t('custom_no_data.text')} />
                                }
                            </>
                        }

                        { isProjectLoading &&
                            <div className={styles.container_loader}>
                                <CustomLoader loaderType="skeleton" isLoading={isProjectLoading}/>
                            </div>
                        }

                        { pageNumber > 1 &&
                            <CustomPagination
                                currentPage={currentPage}
                                setPage={setCurrentPage}
                                pageNumber={pageNumber}
                                isLoading={isProjectLoading}
                            />
                        }
                    </div>

                    <CustomMoreSection isStore />
                    <CustomFooter />
                </>
            }

            { isLoading &&
                <div className={styles.container_loader}>
                    <CustomLoader loaderType="skeleton" isLoading={isLoading}/>
                </div>
            }
        </div>
    )
}

export default Store;